import React, { useState } from 'react';
import { Input, Button, Alert } from '../ui';

interface ClientFormProps {
  initialData?: {
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    notes?: string;
    default_permission?: 'allow_all' | 'deny_all';
  };
  onSubmit: (data: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
    notes?: string;
    default_permission?: 'allow_all' | 'deny_all';
  }) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

export const ClientForm: React.FC<ClientFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Create Client'
}) => {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [color, setColor] = useState(initialData?.color || '#3B82F6');
  const [icon, setIcon] = useState(initialData?.icon || '🖥️');
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [defaultPermission, setDefaultPermission] = useState<'allow_all' | 'deny_all'>(
    initialData?.default_permission || 'allow_all'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Client name is required');
      return;
    }

    try {
      setLoading(true);
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        icon,
        notes: notes.trim() || undefined,
        default_permission: defaultPermission
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Input
        label="Client Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
        placeholder="My Client"
        helperText="A unique name for this client"
      />

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          Description (Optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          rows={3}
          placeholder="Description of this client..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Icon
          </label>
          <input
            type="text"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            placeholder="🖥️"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Color
          </label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="block w-full h-10 px-1 py-1 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          Notes (Optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          rows={2}
          placeholder="Internal notes about this client..."
        />
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          Default Permission
        </label>
        <select
          value={defaultPermission}
          onChange={(e) => setDefaultPermission(e.target.value as 'allow_all' | 'deny_all')}
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <option value="allow_all">Allow All (default)</option>
          <option value="deny_all">Deny All (require explicit permissions)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Controls whether this client can access all resources by default or needs explicit permissions
        </p>
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={!name.trim()}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
